import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import {
  Fuel,
  Truck,
  CheckCircle2,
  AlertCircle,
  XCircle,
  TrendingUp,
  Plus,
  Pencil,
  X,
  QrCode,
  IndianRupee,
  Trash2,
  RotateCcw,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// ---------------------------------------------------------------------------
// Sub-page: Stations master
// ---------------------------------------------------------------------------
function StationsMaster() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fuel/stations");
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = {
        name: (form.name || "").trim(),
        location: form.location || null,
        default_diesel_rate: form.default_diesel_rate ? parseFloat(form.default_diesel_rate) : null,
        upi_id: form.upi_id || null,
        upi_payee_name: form.upi_payee_name || null,
        upi_qr_photo: form.upi_qr_photo || null,
        active: form.active !== false,
      };
      if (!body.name) {
        toast.warning("Name is required");
        return;
      }
      if (form.id) {
        await api.patch(`/fuel/stations/${form.id}`, body);
        toast.success("Station updated");
      } else {
        await api.post("/fuel/stations", body);
        toast.success("Station added");
      }
      setForm(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-3" data-testid="fuel-stations-master">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900">Fuel Stations</h3>
        <button
          onClick={() => setForm({ name: "", active: true })}
          data-testid="add-station-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold"
        >
          <Plus className="h-3.5 w-3.5" /> Add Station
        </button>
      </div>

      <div className="rounded-xl bg-white ring-1 ring-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Location</th>
              <th className="text-right px-3 py-2">Default ₹/L</th>
              <th className="text-left px-3 py-2">Payment Setup</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="text-center text-xs text-slate-500 py-4">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="text-center text-xs text-slate-500 py-6">No stations yet. Add your first.</td></tr>
            )}
            {rows.map((r) => {
              const hasUpi = !!r.upi_id;
              const hasQr = !!r.upi_qr_photo;
              return (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-semibold">{r.name}</td>
                <td className="px-3 py-2 text-slate-600">{r.location || "—"}</td>
                <td className="px-3 py-2 text-right font-mono">{r.default_diesel_rate ? `₹${r.default_diesel_rate}` : "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {hasUpi && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">
                        <QrCode className="h-3 w-3" /> UPI
                      </span>
                    )}
                    {hasQr && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 font-semibold">
                        <QrCode className="h-3 w-3" /> QR Photo
                      </span>
                    )}
                    {!hasUpi && !hasQr && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">
                        ⚠ No Payment Setup
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => setForm({ ...r })}
                    className="text-bravura text-xs font-semibold inline-flex items-center gap-1"
                    data-testid={`edit-station-${r.id}`}
                  >
                    <Pencil className="h-3 w-3" /> Edit
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-end sm:items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl p-5 space-y-3 shadow-xl">
            <div className="flex items-center justify-between">
              <h4 className="text-base font-bold">{form.id ? "Edit Station" : "Add Station"}</h4>
              <button onClick={() => setForm(null)}><X className="h-4 w-4" /></button>
            </div>
            <StationField label="Name *" value={form.name || ""} onChange={(v) => setForm({...form, name: v})} testId="st-name" />
            <StationField label="Location" value={form.location || ""} onChange={(v) => setForm({...form, location: v})} testId="st-location" />
            <StationField label="Default Diesel Rate (₹/L)" type="number" value={form.default_diesel_rate || ""} onChange={(v) => setForm({...form, default_diesel_rate: v})} testId="st-rate" />
            <StationField label="UPI ID (e.g. station@paytm)" value={form.upi_id || ""} onChange={(v) => setForm({...form, upi_id: v})} testId="st-upi" />
            <StationField label="UPI Payee Name" value={form.upi_payee_name || ""} onChange={(v) => setForm({...form, upi_payee_name: v})} testId="st-payee" />
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">
                UPI QR Photo (optional — if no UPI ID)
              </label>
              <input
                type="file"
                accept="image/*"
                className="w-full text-xs"
                data-testid="st-qr-photo"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  if (f.size > 2 * 1024 * 1024) {
                    alert("QR photo must be ≤2 MB");
                    return;
                  }
                  const reader = new FileReader();
                  reader.onload = () => setForm({ ...form, upi_qr_photo: reader.result });
                  reader.readAsDataURL(f);
                }}
              />
              {form.upi_qr_photo && (
                <div className="mt-2 flex items-center gap-2">
                  <img src={form.upi_qr_photo} alt="" className="h-20 w-20 object-contain rounded ring-1 ring-slate-200" />
                  <button onClick={() => setForm({ ...form, upi_qr_photo: "" })} className="text-red-600 text-xs hover:underline">
                    Remove
                  </button>
                </div>
              )}
              <div className="text-[10px] text-slate-500 mt-1">
                Pump ka QR sticker photo upload karein. Driver app me dikhega.
              </div>
            </div>
            <button onClick={save} data-testid="save-station-btn" className="w-full rounded-xl bg-emerald-600 text-white py-2.5 font-semibold">
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StationField({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">{label}</label>
      <input
        type={type}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bravura"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-page: Refills + Approval Queue
// ---------------------------------------------------------------------------
function RefillsTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [photoView, setPhotoView] = useState(null);
  const [editRow, setEditRow] = useState(null);       // {id, litres, amount, ...}
  const [stations, setStations] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fuel/dashboard");
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/fuel/stations").then((r) => setStations(r.data || [])).catch(() => {});
  }, []);

  const act = async (rid, action, reason) => {
    try {
      await api.post(`/fuel/refills/${rid}/approve`, { action, reason: reason || null });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      setRejectId(null); setRejectReason("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const revert = async (rid) => {
    if (!window.confirm("Revert this refill back to Pending status? Any prior approval will be cleared.")) return;
    try {
      await api.post(`/fuel/refills/${rid}/revert-approval`);
      toast.success("Reverted to Pending");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeRefill = async (rid, truck_no) => {
    if (!window.confirm(`Delete this refill of ${truck_no}?\n\nThis is permanent. Historic mileage entries derived from this refill may become stale.`)) return;
    try {
      await api.delete(`/fuel/refills/${rid}`);
      toast.success("Refill deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (!data) return null;

  const filtered = filter === "pending" ? data.pending : data.recent;

  return (
    <div className="space-y-4" data-testid="fuel-refills-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Refills" value={data.kpis.total_refills} Icon={Fuel} accent="amber" />
        <KpiCard label="Pending" value={data.kpis.pending_approvals} Icon={AlertCircle} accent="rose" />
        <KpiCard label="Total Spend" value={formatINR(data.kpis.total_amount)} Icon={IndianRupee} accent="emerald" />
        <KpiCard label="Diesel" value={`${data.kpis.total_litres.toFixed(1)} L`} Icon={Fuel} accent="sky" />
        <KpiCard label="Urea" value={`${data.kpis.urea_litres.toFixed(1)} L`} Icon={Fuel} accent="violet" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {[
          { v: "all", label: "Recent" },
          { v: "pending", label: `Pending (${data.pending?.length || 0})` },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setFilter(t.v)}
            data-testid={`refill-filter-${t.v}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
              filter === t.v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="rounded-xl bg-white ring-1 ring-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Truck</th>
              <th className="text-left px-3 py-2">Driver</th>
              <th className="text-left px-3 py-2">Station</th>
              <th className="text-right px-3 py-2">Diesel</th>
              <th className="text-right px-3 py-2">Total</th>
              <th className="text-center px-3 py-2">Mode</th>
              <th className="text-center px-3 py-2">Mileage</th>
              <th className="text-center px-3 py-2">Photos</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(filtered || []).length === 0 && (
              <tr><td colSpan={10} className="text-center text-xs text-slate-400 py-6">No records</td></tr>
            )}
            {(filtered || []).map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-3 py-2 text-xs whitespace-nowrap">{r.refill_date}</td>
                <td className="px-3 py-2 font-semibold">{r.truck_no}</td>
                <td className="px-3 py-2 text-xs">{r.driver_name || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-600 truncate max-w-[160px]">{r.station_name || "—"}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{r.diesel_litres} L</td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{formatINR(r.total_amount)}</td>
                <td className="px-3 py-2 text-center">
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 uppercase">
                    {r.payment_mode}
                  </span>
                </td>
                <td className="px-3 py-2 text-center text-xs">
                  {r.mileage_kmpl ? <span className="text-emerald-600 font-semibold">{r.mileage_kmpl}</span> : "—"}
                </td>
                <td className="px-3 py-2 text-center">
                  <div className="flex gap-1 justify-center items-center">
                    {r.payment_proof_photo && (
                      <button
                        onClick={() => setPhotoView({ url: r.payment_proof_photo, label: "UPI / Payment Proof" })}
                        title="Payment proof"
                        className="h-7 w-7 rounded ring-1 ring-emerald-300 overflow-hidden"
                        data-testid={`view-pay-${r.id}`}
                      >
                        <img src={r.payment_proof_photo} alt="" className="h-full w-full object-cover" />
                      </button>
                    )}
                    {r.pump_qr_photo && (
                      <button
                        onClick={() => setPhotoView({
                          url: r.pump_qr_photo,
                          label: "Pump QR (driver-uploaded)",
                          refill_id: r.id,
                          station_id: r.station_id,
                          promotable: true,
                        })}
                        title="Pump QR uploaded by driver — click to view + promote"
                        className="h-7 w-7 rounded ring-2 ring-amber-400 overflow-hidden relative"
                        data-testid={`view-pump-qr-${r.id}`}
                      >
                        <img src={r.pump_qr_photo} alt="" className="h-full w-full object-cover" />
                        <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[8px] font-bold rounded-full h-3.5 w-3.5 flex items-center justify-center">!</span>
                      </button>
                    )}
                    {(r.bill_photos || []).filter(Boolean).slice(0, 2).map((p, i) => (
                      <button
                        key={i}
                        onClick={() => setPhotoView({ url: p, label: `Bill / Photo ${i + 1}` })}
                        title={`Bill ${i + 1}`}
                        className="h-7 w-7 rounded ring-1 ring-slate-300 overflow-hidden"
                        data-testid={`view-bill-${r.id}-${i}`}
                      >
                        <img src={p} alt="" className="h-full w-full object-cover" />
                      </button>
                    ))}
                    {(r.bill_photos || []).filter(Boolean).length > 2 && (
                      <span className="text-[10px] text-slate-500">
                        +{(r.bill_photos || []).filter(Boolean).length - 2}
                      </span>
                    )}
                    {!r.payment_proof_photo && !r.pump_qr_photo &&
                      (!r.bill_photos || r.bill_photos.filter(Boolean).length === 0) && (
                      <span className="text-[10px] text-slate-300">—</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex gap-1 justify-end items-center flex-wrap">
                    {r.status === "pending" ? (
                      <>
                        <button
                          onClick={() => act(r.id, "approve")}
                          data-testid={`approve-${r.id}`}
                          className="px-2 py-1 rounded-md bg-emerald-600 text-white text-[11px] font-semibold"
                        >Approve</button>
                        <button
                          onClick={() => setRejectId(r.id)}
                          data-testid={`reject-${r.id}`}
                          className="px-2 py-1 rounded-md bg-red-600 text-white text-[11px] font-semibold"
                        >Reject</button>
                      </>
                    ) : (
                      <>
                        <StatusBadge status={r.status} />
                        <button
                          onClick={() => revert(r.id)}
                          title="Revert to Pending"
                          data-testid={`revert-${r.id}`}
                          className="p-1 rounded hover:bg-amber-50 text-amber-700"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setEditRow({
                        id: r.id,
                        truck_no: r.truck_no,
                        litres: r.diesel_litres,
                        amount: r.total_amount,
                        odometer_km: r.odometer_km || "",
                        station_id: r.station_id || "",
                        payment_mode: r.payment_mode || "",
                        remarks: r.remarks || "",
                      })}
                      title="Edit refill"
                      data-testid={`edit-refill-${r.id}`}
                      className="p-1 rounded hover:bg-slate-100 text-slate-500"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => removeRefill(r.id, r.truck_no)}
                      title="Delete refill"
                      data-testid={`delete-refill-${r.id}`}
                      className="p-1 rounded hover:bg-red-50 text-red-500"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* By Truck */}
      <div>
        <h3 className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-1.5">
          <Truck className="h-4 w-4" /> Spending by Truck
        </h3>
        <div className="rounded-xl bg-white ring-1 ring-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Truck</th>
                <th className="text-right px-3 py-2">Refills</th>
                <th className="text-right px-3 py-2">Litres</th>
                <th className="text-right px-3 py-2">Spend</th>
                <th className="text-right px-3 py-2">Mileage</th>
              </tr>
            </thead>
            <tbody>
              {(data.by_truck || []).length === 0 && (
                <tr><td colSpan={5} className="text-center text-xs text-slate-400 py-6">No data</td></tr>
              )}
              {(data.by_truck || []).map((t) => (
                <tr key={t.truck_no} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-semibold">{t.truck_no}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{t.refills}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{t.litres} L</td>
                  <td className="px-3 py-2 text-right font-semibold">{formatINR(t.amount)}</td>
                  <td className="px-3 py-2 text-right text-xs">
                    {t.mileage_kmpl ? (
                      <span className="text-emerald-600 font-semibold">{t.mileage_kmpl} km/L</span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reject modal */}
      {rejectId && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl p-5 space-y-3">
            <h4 className="text-base font-bold">Reject Refill</h4>
            <textarea
              rows={3}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              placeholder="Reason (optional)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="reject-reason"
            />
            <div className="flex gap-2">
              <button onClick={() => { setRejectId(null); setRejectReason(""); }} className="flex-1 rounded-lg bg-slate-100 py-2 text-sm font-semibold">
                Cancel
              </button>
              <button onClick={() => act(rejectId, "reject", rejectReason)} data-testid="confirm-reject" className="flex-1 rounded-lg bg-red-600 text-white py-2 text-sm font-semibold">
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo preview modal */}
      {photoView && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setPhotoView(null)}
          data-testid="photo-preview-modal"
        >
          <div
            className="relative max-w-3xl w-full max-h-[90vh] bg-white rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200">
              <div className="text-sm font-bold text-slate-900">
                {photoView.label}
              </div>
              <button
                onClick={() => setPhotoView(null)}
                className="text-slate-500 text-2xl leading-none px-2"
                data-testid="photo-preview-close"
              >
                ×
              </button>
            </div>
            <img
              src={photoView.url}
              alt={photoView.label}
              className="w-full max-h-[80vh] object-contain bg-slate-100"
            />
            <div className="px-4 py-2 text-center flex items-center justify-center gap-3">
              <a
                href={photoView.url}
                download
                className="text-xs text-bravura-700 hover:underline"
              >
                Download full image →
              </a>
              {photoView.promotable && photoView.station_id && (
                <button
                  onClick={async () => {
                    if (!window.confirm("Set this QR as station's default? Future drivers will see this QR for this pump.")) return;
                    try {
                      await api.post(`/fuel/refills/${photoView.refill_id}/promote-pump-qr`);
                      toast.success("Promoted to station default ✓");
                      setPhotoView(null);
                      load();
                    } catch (e) {
                      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
                    }
                  }}
                  data-testid="promote-pump-qr-btn"
                  className="text-xs bg-emerald-600 text-white px-3 py-1.5 rounded-lg font-semibold hover:bg-emerald-700"
                >
                  ⬆ Set as Station QR
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Refill Dialog */}
      {editRow && (
        <EditRefillDialog
          row={editRow}
          stations={stations}
          onClose={() => setEditRow(null)}
          onSaved={() => { setEditRow(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit Refill Dialog — admin adjusts driver-submitted data
// ---------------------------------------------------------------------------
function EditRefillDialog({ row, stations, onClose, onSaved }) {
  const [form, setForm] = useState({
    litres: row.litres ?? "",
    amount: row.amount ?? "",
    odometer_km: row.odometer_km ?? "",
    station_id: row.station_id ?? "",
    payment_mode: row.payment_mode ?? "",
    remarks: row.remarks ?? "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        litres: form.litres === "" ? null : parseFloat(form.litres),
        amount: form.amount === "" ? null : parseFloat(form.amount),
        odometer_km: form.odometer_km === "" ? null : parseInt(form.odometer_km, 10),
        station_id: form.station_id || null,
        payment_mode: form.payment_mode || null,
        remarks: form.remarks || null,
      };
      // strip nulls so only-changed fields go via exclude_unset semantics on backend
      Object.keys(payload).forEach((k) => { if (payload[k] === null) delete payload[k]; });
      await api.patch(`/fuel/refills/${row.id}`, payload);
      toast.success("Refill updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="edit-refill-dialog">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold">Edit Refill — {row.truck_no}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Litres</label>
            <input
              type="number" step="0.01" value={form.litres}
              onChange={(e) => setForm({ ...form, litres: e.target.value })}
              data-testid="edit-refill-litres"
              className="w-full mt-1 rounded-lg ring-1 ring-slate-300 px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Amount (₹)</label>
            <input
              type="number" step="0.01" value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              data-testid="edit-refill-amount"
              className="w-full mt-1 rounded-lg ring-1 ring-slate-300 px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Odometer (km)</label>
            <input
              type="number" value={form.odometer_km}
              onChange={(e) => setForm({ ...form, odometer_km: e.target.value })}
              data-testid="edit-refill-odometer"
              className="w-full mt-1 rounded-lg ring-1 ring-slate-300 px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Payment mode</label>
            <select
              value={form.payment_mode}
              onChange={(e) => setForm({ ...form, payment_mode: e.target.value })}
              data-testid="edit-refill-mode"
              className="w-full mt-1 rounded-lg ring-1 ring-slate-300 px-2 py-1.5 text-sm"
            >
              <option value="">—</option>
              <option value="upi">UPI</option>
              <option value="card">Card</option>
              <option value="cash">Cash</option>
              <option value="credit">Credit</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Station</label>
            <select
              value={form.station_id}
              onChange={(e) => setForm({ ...form, station_id: e.target.value })}
              data-testid="edit-refill-station"
              className="w-full mt-1 rounded-lg ring-1 ring-slate-300 px-2 py-1.5 text-sm"
            >
              <option value="">— (keep as-is / clear)</option>
              {stations.map((s) => (
                <option key={s.id} value={s.id}>{s.name}{s.location ? ` · ${s.location}` : ""}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Admin remarks</label>
            <textarea
              value={form.remarks} rows={2}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              data-testid="edit-refill-remarks"
              className="w-full mt-1 rounded-lg ring-1 ring-slate-300 px-2 py-1.5 text-sm"
              placeholder="Why edited — audit trail"
            />
          </div>
        </div>
        {form.litres && form.amount && parseFloat(form.litres) > 0 && (
          <div className="text-[11px] text-slate-500">
            Auto-calculated ₹/L: <b className="text-slate-900">
              {(parseFloat(form.amount) / parseFloat(form.litres)).toFixed(2)}
            </b>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="rounded-lg ring-1 ring-slate-300 px-3 py-1.5 text-xs font-semibold">Cancel</button>
          <button
            onClick={save} disabled={busy}
            data-testid="edit-refill-save"
            className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-1.5 text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, Icon, accent }) {
  const map = {
    amber: "from-bravura-50 to-white ring-bravura-200 text-bravura-700",
    rose: "from-red-50 to-white ring-red-200 text-red-700",
    emerald: "from-emerald-50 to-white ring-emerald-200 text-emerald-700",
    sky: "from-sky-50 to-white ring-sky-200 text-sky-700",
    violet: "from-violet-50 to-white ring-violet-200 text-violet-700",
  };
  return (
    <div className={`rounded-xl bg-gradient-to-b ${map[accent] || map.amber} ring-1 p-3`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className="text-xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    approved: { c: "bg-emerald-100 text-emerald-700", t: "Approved" },
    rejected: { c: "bg-red-100 text-red-700", t: "Rejected" },
    pending: { c: "bg-bravura-100 text-bravura-700", t: "Pending" },
  };
  const m = map[status] || { c: "bg-slate-100 text-slate-600", t: status };
  return <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${m.c} font-semibold`}>{m.t}</span>;
}

// ---------------------------------------------------------------------------
// Main FuelAdmin component (used as a tab inside LogisticsHub or standalone)
// ---------------------------------------------------------------------------
import FuelSettlements from "@/pages/FuelSettlements";

export default function FuelAdmin() {
  const [tab, setTab] = useState("refills");
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  return (
    <div className="space-y-4" data-testid="fuel-admin">
      {/* Mobile: stack header + tabs; sm+: side by side */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Fuel &amp; Mileage
          </h1>
          <p className="text-xs text-slate-500">Driver refills, approvals, mileage tracking &amp; monthly settlement</p>
        </div>
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg overflow-x-auto no-scrollbar self-start">
          <button
            onClick={() => setTab("refills")}
            data-testid="tab-refills"
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition flex-shrink-0 ${
              tab === "refills" ? "bg-white text-slate-900 shadow" : "text-slate-500"
            }`}
          >
            Refills
          </button>
          <button
            onClick={() => setTab("settlements")}
            data-testid="tab-settlements"
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition flex-shrink-0 ${
              tab === "settlements" ? "bg-white text-slate-900 shadow" : "text-slate-500"
            }`}
          >
            Settlements
          </button>
          <button
            onClick={() => setTab("stations")}
            data-testid="tab-stations"
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition flex-shrink-0 ${
              tab === "stations" ? "bg-white text-slate-900 shadow" : "text-slate-500"
            }`}
          >
            Stations
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => setTab("danger")}
              data-testid="tab-danger"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition flex-shrink-0 ${
                tab === "danger" ? "bg-red-600 text-white shadow" : "text-red-600"
              }`}
            >
              Danger Zone
            </button>
          )}
        </div>
      </div>

      {tab === "refills" && <RefillsTab />}
      {tab === "settlements" && <FuelSettlements />}
      {tab === "stations" && <StationsMaster />}
      {tab === "danger" && isSuperAdmin && <DangerZone />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Danger Zone — Super Admin only. One-shot bulk reset of fuel trial data.
// ---------------------------------------------------------------------------
function DangerZone() {
  const [opts, setOpts] = useState({
    include_refills: true,
    include_mileage: true,
    include_settlements: true,
    include_stations: false,
  });
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const run = async () => {
    if (confirm !== "RESET_FUEL_DATA") {
      toast.error("Type RESET_FUEL_DATA exactly to enable the reset");
      return;
    }
    if (!window.confirm(
      "This will PERMANENTLY DELETE all selected fuel data across the whole company.\n\n" +
      "Deletions cannot be undone. Ensure you have a database backup.\n\n" +
      "Proceed?"
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/fuel/_admin/reset-all", {
        confirm: "RESET_FUEL_DATA",
        ...opts,
      });
      setLastResult(data.deleted_counts);
      setConfirm("");
      toast.success("Fuel data reset complete. Refresh other tabs to see the empty state.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (k) => setOpts((p) => ({ ...p, [k]: !p[k] }));

  return (
    <div className="space-y-4" data-testid="fuel-danger-zone">
      <div className="rounded-xl ring-2 ring-red-300 bg-red-50 p-5 space-y-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-8 w-8 text-red-600 flex-shrink-0" />
          <div>
            <h2 className="text-lg font-bold text-red-900">Reset Trial Fuel Data</h2>
            <p className="text-xs text-red-800 mt-1">
              Aap kar rahe hain: <b>PERMANENT DELETE</b> of all selected fuel collections
              across the entire company. Use this ONLY when moving from trial to production
              data. Once done, drivers can start submitting fresh refills. Post-deploy audit
              trail retains the reset event (who, when, what got deleted).
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 ring-1 ring-red-200 space-y-2">
          <div className="text-xs font-bold text-slate-700 mb-2">What to clear:</div>
          {[
            { k: "include_refills", label: "Fuel Refills (driver submissions + approvals)", warn: true },
            { k: "include_mileage", label: "Mileage / km/L history (derived from refills)", warn: true },
            { k: "include_settlements", label: "Fuel Settlements + settlement lines", warn: true },
            { k: "include_stations", label: "Fuel Stations (pump master) — WILL BREAK legacy refill lookups", warn: false },
          ].map(({ k, label, warn }) => (
            <label key={k} className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer hover:bg-slate-50 rounded p-1">
              <input
                type="checkbox" checked={opts[k]}
                onChange={() => toggle(k)}
                data-testid={`danger-toggle-${k}`}
                className="mt-0.5"
              />
              <span>
                {label}
                {opts[k] && warn && <span className="ml-1 text-red-600 font-semibold">· selected</span>}
              </span>
            </label>
          ))}
        </div>

        <div className="bg-white rounded-lg p-4 ring-1 ring-red-300">
          <label className="text-[10px] uppercase tracking-widest text-red-700 font-bold block">
            Type <span className="font-mono">RESET_FUEL_DATA</span> to enable
          </label>
          <input
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="RESET_FUEL_DATA"
            data-testid="danger-confirm-input"
            className="w-full mt-1 rounded-lg ring-1 ring-slate-300 px-3 py-2 text-sm font-mono"
          />
        </div>

        <button
          onClick={run}
          disabled={busy || confirm !== "RESET_FUEL_DATA"}
          data-testid="danger-reset-btn"
          className="w-full rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-3 text-sm font-bold flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          {busy ? "Deleting…" : "Reset Fuel Data — permanent"}
        </button>
      </div>

      {lastResult && (
        <div className="rounded-xl ring-1 ring-emerald-200 bg-emerald-50 p-4 space-y-2" data-testid="danger-last-result">
          <div className="flex items-center gap-2 text-emerald-800 font-bold text-sm">
            <CheckCircle2 className="h-4 w-4" /> Reset complete
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="py-1">Collection</th>
                <th className="py-1 text-right">Rows deleted</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(lastResult).map(([k, v]) => (
                <tr key={k} className="border-t border-emerald-100">
                  <td className="py-1 font-mono">{k}</td>
                  <td className="py-1 text-right font-mono font-bold">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
